import { Message } from './chatService';
import { supabase } from '@/integrations/supabase/client';
import { API_URL } from '@/lib/apiClient';
import {
  PREPARADOR_PROMPTS,
  cuerpoFromChatMode,
  cuerpoFromPlan,
  type CuerpoId,
} from '@/data/preparadorPrompts';

type StreamChatParams = {
  messages: { role: 'user' | 'assistant'; content: string }[];
  chatId: string;
  plan?: string;
  /** Modo efectivo del chat (oposiciones_c1, oposiciones_iipp, etc.). Se usa para inyectar el preparador correcto. */
  chatMode?: string;
  files?: Array<{ name: string; content: string; type: string }>;
  onDelta: (deltaText: string) => void;
  onDone: (metadata?: any) => void;
  onError?: (error: Error) => void;
};

/** Resuelve el cuerpo de oposición desde el chat_mode o el plan. */
function resolveCuerpo(chatMode?: string, plan?: string): CuerpoId | null {
  return cuerpoFromChatMode(chatMode) ?? cuerpoFromPlan(plan);
}

// Map plan types to agent codes in ia_agents table
function getAgentCodeFromPlan(plan?: string): string {
  switch (plan) {
    case 'oposiciones':
    case 'oposiciones_policia_nacional':
    case 'policia-nacional':
      return 'opos';
    case 'pago':
      return 'pro';
    case 'free':
    case 'ayuntamiento':
    default:
      return 'free';
  }
}

function getFallbackEndpoint(plan?: string): string {
  const vpsBase = API_URL;
  return (plan === 'oposiciones' || plan === 'oposiciones_policia_nacional' || plan === 'policia-nacional')
    ? `${vpsBase}/chat-oposiciones`
    : `${vpsBase}/chat-anythingllm`;
}

// Get edge function URL as fallback when VPS is down
function getEdgeFunctionFallback(plan?: string): string {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  return plan === 'oposiciones'
    ? `${supabaseUrl}/functions/v1/chat-oposiciones`
    : `${supabaseUrl}/functions/v1/chat-anythingllm`;
}

// In-memory cache for agent endpoints (TTL: 5 minutes)
const agentEndpointCache: Record<string, { url: string; timestamp: number }> = {};
const AGENT_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Dynamically fetch agent endpoint from ia_agents table (cached)
async function getAgentEndpoint(plan?: string): Promise<string> {
  const baseUrl = API_URL;
  const agentCode = getAgentCodeFromPlan(plan);

  // Return cached endpoint if still valid
  const cached = agentEndpointCache[agentCode];
  if (cached && Date.now() - cached.timestamp < AGENT_CACHE_TTL) {
    return cached.url;
  }

  try {
    const { data: agent, error } = await supabase
      .from('ia_agents')
      .select('endpoint_url, active')
      .eq('code', agentCode)
      .eq('active', true)
      .single();

    if (error) {
      console.warn('Error fetching agent from ia_agents:', error.message);
      return getFallbackEndpoint(plan);
    }

    if (agent && agent.endpoint_url) {
      let resolvedUrl: string;

      if (agent.endpoint_url.startsWith('http')) {
        // Full URL — use directly (points to VPS API)
        resolvedUrl = agent.endpoint_url;
      } else {
        // Relative path — append to VPS base
        resolvedUrl = `${API_URL}/${agent.endpoint_url.replace(/^\//, '')}`;
      }

      // Cache the resolved URL
      agentEndpointCache[agentCode] = { url: resolvedUrl, timestamp: Date.now() };
      return resolvedUrl;
    }
  } catch (error) {
    console.warn('Error fetching agent endpoint, using fallback:', error);
  }

  return getFallbackEndpoint(plan);
}

// In-memory cached access token to avoid hitting localStorage + getSession on every call
let cachedToken: { token: string; expiresAt: number } | null = null;

function isRefreshTokenError(err: any): boolean {
  const msg = (err?.message || '').toLowerCase();
  return (
    msg.includes('refresh_token_not_found') ||
    msg.includes('invalid refresh token') ||
    err?.code === 'refresh_token_not_found' ||
    err?.code === 'session_not_found'
  );
}

async function handleSessionExpired(): Promise<never> {
  cachedToken = null;
  try {
    await supabase.auth.signOut({ scope: 'local' as any });
  } catch { /* noop */ }
  const e = new Error('SESSION_EXPIRED');
  (e as any).code = 'SESSION_EXPIRED';
  throw e;
}

async function getValidAccessToken(): Promise<string> {
  const nowSec = Math.floor(Date.now() / 1000);

  // Reuse cached token if it's still valid for ≥60s
  if (cachedToken && cachedToken.expiresAt - nowSec > 60) {
    return cachedToken.token;
  }

  let session;
  try {
    const result = await supabase.auth.getSession();
    session = result.data.session;
  } catch (err) {
    if (isRefreshTokenError(err)) await handleSessionExpired();
    throw err;
  }

  // Refresh proactively if expiring soon
  if (session?.expires_at && session.expires_at - nowSec < 60) {
    try {
      const { data: refreshed, error } = await supabase.auth.refreshSession();
      if (error) {
        if (isRefreshTokenError(error)) await handleSessionExpired();
        throw error;
      }
      if (refreshed.session) session = refreshed.session;
    } catch (err) {
      if (isRefreshTokenError(err)) await handleSessionExpired();
      throw err;
    }
  }

  if (!session) {
    cachedToken = null;
    const e = new Error('SESSION_EXPIRED');
    (e as any).code = 'SESSION_EXPIRED';
    throw e;
  }

  cachedToken = {
    token: session.access_token,
    expiresAt: session.expires_at ?? nowSec + 3600,
  };
  return session.access_token;
}

// Pre-warm: resolve auth + agent endpoint as soon as the chat surface mounts
export async function prewarmChat(plan?: string): Promise<void> {
  try {
    await Promise.all([getValidAccessToken(), getAgentEndpoint(plan)]);
  } catch {
    /* silent — best effort */
  }
}

async function fetchWithTokenRefresh(
  endpoint: string,
  body: any,
  accessToken: string,
  retryCount = 0
): Promise<Response> {
  // If calling a Supabase edge function, add the apikey header
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
  const isEdgeFunction = endpoint.startsWith(supabaseUrl) && endpoint.includes('/functions/v1/');

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`,
  };

  if (isEdgeFunction) {
    headers['apikey'] = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  }

  const resp = await fetch(endpoint, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });

  // If we get 401 and haven't retried yet, try refreshing the token
  if (resp.status === 401 && retryCount < 1) {
    console.log('Received 401, attempting token refresh...');
    try {
      const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();

      if (refreshError) {
        if (isRefreshTokenError(refreshError)) await handleSessionExpired();
        throw refreshError;
      }
      if (!refreshData.session) {
        await handleSessionExpired();
      }

      cachedToken = {
        token: refreshData.session!.access_token,
        expiresAt: refreshData.session!.expires_at ?? Math.floor(Date.now() / 1000) + 3600,
      };
      console.log('Token refreshed, retrying request...');
      return fetchWithTokenRefresh(endpoint, body, refreshData.session!.access_token, retryCount + 1);
    } catch (err) {
      if (isRefreshTokenError(err)) await handleSessionExpired();
      throw err;
    }
  }

  return resp;
}

// Map plan → cache scope
function getCacheScope(plan?: string): { chat_mode: string; exam_type: string | null } {
  if (plan === 'oposiciones') return { chat_mode: 'oposiciones', exam_type: null };
  if (plan === 'oposiciones_c1') return { chat_mode: 'oposiciones', exam_type: 'c1' };
  if (plan === 'oposiciones_policia_nacional' || plan === 'policia-nacional') return { chat_mode: 'oposiciones', exam_type: 'policia_nacional' };
  return { chat_mode: 'general', exam_type: null };
}

async function lookupSemanticCache(
  question: string,
  plan: string | undefined,
  accessToken: string,
): Promise<{ answer: string; sources?: any[]; similarity: number } | null> {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const scope = getCacheScope(plan);
    const resp = await fetch(`${supabaseUrl}/functions/v1/chat-cache-lookup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      },
      body: JSON.stringify({ action: 'lookup', question, ...scope }),
    });
    if (!resp.ok) return null;
    const data = await resp.json();
    return data.hit ? data : null;
  } catch (e) {
    console.warn('[cache] lookup failed:', e);
    return null;
  }
}

function storeSemanticCache(
  question: string,
  answer: string,
  plan: string | undefined,
  accessToken: string,
  sources?: any[],
) {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const scope = getCacheScope(plan);
    fetch(`${supabaseUrl}/functions/v1/chat-cache-lookup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      },
      body: JSON.stringify({ action: 'store', question, answer, sources, ...scope }),
    }).catch(() => {});
  } catch { /* noop */ }
}

export async function streamChat({ messages, chatId, plan, chatMode, files, onDelta, onDone, onError }: StreamChatParams) {
  try {
    const t0 = performance.now();
    const accessToken = await getValidAccessToken();
    const tAuth = performance.now();
    console.log(`⏱️ [AI] Auth resolved in ${(tAuth - t0).toFixed(0)}ms`);

    // ─── Resolver cuerpo + preparador prompt (TODOS los turnos para mantener identidad) ───
    const cuerpo = resolveCuerpo(chatMode, plan);
    const preparadorSystemPrompt = cuerpo ? PREPARADOR_PROMPTS[cuerpo] : null;

    // ─── Semantic cache lookup (only on first turn, no files) ───
    const lastUserMsg = messages.filter(m => m.role === 'user').slice(-1)[0]?.content ?? '';
    const userTurns = messages.filter(m => m.role === 'user').length;
    const canUseCache = !files?.length && userTurns <= 1 && lastUserMsg.length >= 12;

    let cachedAnswerToStore: { question: string } | null = null;
    if (canUseCache) {
      const tCacheStart = performance.now();
      const hit = await lookupSemanticCache(lastUserMsg, plan, accessToken);
      console.log(`⏱️ [AI] Cache: ${(performance.now() - tCacheStart).toFixed(0)}ms ${hit ? `HIT ${(hit.similarity * 100).toFixed(1)}%` : 'miss'}`);

      if (hit) {
        const text = hit.answer;
        const CHUNK = 40;
        for (let i = 0; i < text.length; i += CHUNK) {
          onDelta(text.slice(i, i + CHUNK));
          await new Promise(r => setTimeout(r, 8));
        }
        onDone(hit.sources?.length ? { sources: hit.sources, fromCache: true } : { fromCache: true });
        return;
      }
      // miss: marcar para guardar tras la respuesta del VPS
      cachedAnswerToStore = { question: lastUserMsg };
    }

    // ─── Endpoint resolution ───
    // Si chatMode pertenece a una oposición concreta (incluida Secretaría),
    // forzamos chat-oposiciones para garantizar que el VPS enrute al workspace correcto.
    const isOposicionesMode = typeof chatMode === 'string' && chatMode.startsWith('oposiciones');
    const endpoint = isOposicionesMode
      ? `${API_URL}/chat-oposiciones`
      : await getAgentEndpoint(plan);

    let resp: Response;
    let usedFallback = false;

    const requestBody: Record<string, unknown> = { messages, chatId, files, chatMode };
    if (preparadorSystemPrompt) {
      requestBody.systemPrompt = preparadorSystemPrompt;
      requestBody.cuerpo = cuerpo;
    }

    try {
      resp = await fetchWithTokenRefresh(endpoint, requestBody, accessToken);

      if (resp.status === 404 || resp.status === 502 || resp.status === 503 || resp.status === 401) {
        throw new Error(`VPS error (${resp.status})`);
      }
    } catch (primaryError) {
      usedFallback = true;
      console.warn('VPS unreachable, falling back to edge function:', primaryError);
      const fallbackUrl = isOposicionesMode
        ? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat-oposiciones`
        : getEdgeFunctionFallback(plan);
      resp = await fetchWithTokenRefresh(fallbackUrl, requestBody, accessToken);
    }

    const tFirstByte = performance.now();
    console.log(`⏱️ [AI] TTFB: ${(tFirstByte - tAuth).toFixed(0)}ms ${usedFallback ? '(FALLBACK)' : '(VPS)'}`);
    let fullAnswer = '';
    let firstTokenTime: number | null = null;

    if (!resp.ok) {
      const errorData = await resp.json().catch(() => ({ error: "Error desconocido" }));
      throw new Error(errorData.error || `Error ${resp.status}`);
    }

    if (!resp.body) {
      throw new Error("No response body");
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let textBuffer = "";
    let streamDone = false;
    let capturedMetadata: any = null;

    while (!streamDone) {
      const { done, value } = await reader.read();
      if (done) break;
      textBuffer += decoder.decode(value, { stream: true });

      let newlineIndex: number;
      while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
        let line = textBuffer.slice(0, newlineIndex);
        textBuffer = textBuffer.slice(newlineIndex + 1);

        if (line.endsWith("\r")) line = line.slice(0, -1);
        if (line.startsWith(":") || line.trim() === "") continue;
        if (!line.startsWith("data: ")) continue;

        const jsonStr = line.slice(6).trim();
        if (jsonStr === "[DONE]") {
          streamDone = true;
          break;
        }

        try {
          const parsed = JSON.parse(jsonStr);
          const content = parsed.choices?.[0]?.delta?.content as string | undefined;
          const metadata = parsed.choices?.[0]?.delta?.metadata;

          if (content) {
            if (!firstTokenTime) {
              firstTokenTime = performance.now();
              console.log(`⏱️ [AI] Time to first token: ${(firstTokenTime - t0).toFixed(0)}ms`);
            }
            fullAnswer += content;
            onDelta(content);
          }
          if (metadata) capturedMetadata = metadata;
        } catch {
          textBuffer = line + "\n" + textBuffer;
          break;
        }
      }
    }

    // Final flush
    if (textBuffer.trim()) {
      for (let raw of textBuffer.split("\n")) {
        if (!raw) continue;
        if (raw.endsWith("\r")) raw = raw.slice(0, -1);
        if (raw.startsWith(":") || raw.trim() === "") continue;
        if (!raw.startsWith("data: ")) continue;
        const jsonStr = raw.slice(6).trim();
        if (jsonStr === "[DONE]") continue;
        try {
          const parsed = JSON.parse(jsonStr);
          const content = parsed.choices?.[0]?.delta?.content as string | undefined;
          const metadata = parsed.choices?.[0]?.delta?.metadata;

          if (content) {
            fullAnswer += content;
            onDelta(content);
          }
          if (metadata) capturedMetadata = metadata;
        } catch {
          /* ignore partial leftovers */
        }
      }
    }

    const tEnd = performance.now();
    console.log(`⏱️ [AI] Total stream time: ${(tEnd - t0).toFixed(0)}ms | First token: ${firstTokenTime ? (firstTokenTime - t0).toFixed(0) + 'ms' : 'N/A'}`);

    // Store in semantic cache (fire-and-forget) if eligible
    if (cachedAnswerToStore && fullAnswer.length >= 80) {
      storeSemanticCache(
        cachedAnswerToStore.question,
        fullAnswer,
        plan,
        accessToken,
        capturedMetadata?.sources,
      );
    }

    onDone(capturedMetadata);
  } catch (error) {
    console.error("Stream chat error:", error);
    const isSessionExpired = (error as any)?.code === 'SESSION_EXPIRED' || (error instanceof Error && error.message === 'SESSION_EXPIRED');
    if (isSessionExpired) {
      const friendly = new Error('Tu sesión ha caducado. Vuelve a iniciar sesión para continuar.');
      (friendly as any).code = 'SESSION_EXPIRED';
      // Toast con acción a login (lazy import para no acoplar)
      try {
        const { toast } = await import('sonner');
        const loginPath = window.location.pathname.startsWith('/oposiciones')
          ? '/auth/oposiciones/login'
          : '/auth/funcionarios/login';
        toast.error('Tu sesión ha caducado', {
          description: 'Vuelve a iniciar sesión para continuar.',
          action: { label: 'Iniciar sesión', onClick: () => { window.location.href = loginPath; } },
          duration: 8000,
        });
      } catch { /* noop */ }
      if (onError) { onError(friendly); return; }
      throw friendly;
    }
    if (onError) {
      onError(error instanceof Error ? error : new Error("Error desconocido"));
      return; // Error ya manejado por el callback, no re-lanzar
    }
    throw error; // Solo re-lanzar si no hay onError handler
  }
}

export function formatMessagesForAI(messages: Message[]): { role: 'user' | 'assistant'; content: string }[] {
  return messages.map(msg => ({
    role: msg.role as 'user' | 'assistant',
    content: msg.content
  }));
}
